import { AdControls } from '@post-office/components--ad-controls';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { RovoIcon } from '@atlaskit/logo';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { usePublish } from '@atlaskit/rovo-triggers';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { type JSX } from 'react/jsx-runtime';

import { messages } from '../messages';

// The rovo_chat_non_mau_jira_confluence_cta experiment shipped v1 as the winning
// variant. Retained as a static analytics attribute so downstream dashboards
// keep receiving the `variant` field.
const VARIANT = 'v1';

const styles = cssMap({
	boxContainer: {
		display: 'flex',
		width: '100%',
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		alignItems: 'flex-start',
		gap: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.information'),
		placeContent: 'center',
	},
	contentStack: {
		gap: token('space.100'),
		width: '100%',
	},
	headerRow: {
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
	},
	ctaLink: {
		backgroundColor: token('color.background.neutral.subtle'),
		padding: token('space.0'),
		borderRadius: token('radius.xsmall'),
		color: token('color.link'),
		cursor: 'pointer',
		font: token('font.body.small'),
		textAlign: 'left',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
});

const ACTION_SUBJECT = 'rovoNonMauChatCta';

type NonMauChatCtaMessageProps = {
	context: {
		source: string;
		[key: string]: unknown;
	};
	handleOnDismissed: () => void;
};

export const NonMauChatCtaMessage = ({
	context,
	handleOnDismissed,
}: NonMauChatCtaMessageProps): JSX.Element => {
	const { source } = context;

	const { formatMessage } = useIntl();
	const { messageClicked } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const chatEventPublish = usePublish('ai-mate');

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'rendered',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: VARIANT,
				source,
			},
		});
	}, [fireAnalyticsEvent, source]);

	const handleDismiss = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'dismissed',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: VARIANT,
				source,
			},
		});
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				experimentCohort: VARIANT,
				source,
			},
		});
		handleOnDismissed();
	}, [fireAnalyticsEvent, source, messageClicked, messageInstanceClicked, handleOnDismissed]);

	const handleCtaClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: ACTION_SUBJECT,
			attributes: {
				variant: VARIANT,
				source,
			},
		});
		void messageClicked();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'openRovoChat',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'open',
			},
			additionalMetadataFromFrontend: {
				experimentCohort: VARIANT,
				source,
			},
		});
		chatEventPublish({
			type: 'chat-new',
			source: 'rovoNonMauChatCta',
			data: {
				prompt: formatMessage(messages.chatPrompt),
				dialogues: [],
			},
		});
	}, [fireAnalyticsEvent, source, messageClicked, messageInstanceClicked, chatEventPublish]);

	return (
		<Box xcss={cx(styles.boxContainer)} testId="rovo-non-mau-chat-cta">
			<Stack xcss={cx(styles.contentStack)} testId="rovo-non-mau-chat-cta-content">
				<Inline xcss={cx(styles.headerRow)}>
					<RovoIcon appearance="brand" size="xsmall" shouldUseHexLogo={true} />
					<AdControls
						showWhyAmISeeingThis={true}
						whyAmISeeingThisModalContent={{
							reasons: [
								<FormattedMessage key="reason-1" {...messages.whyAmISeeingThisReason1} />,
								<FormattedMessage key="reason-2" {...messages.whyAmISeeingThisReason2} />,
							],
						}}
						onDismissed={handleDismiss}
						spacing="compact"
					/>
				</Inline>
				<Heading size="xsmall">{formatMessage(messages.heading)}</Heading>
				<Text size="small" color="color.text.subtlest">
					{formatMessage(messages.description)}
				</Text>
				<Pressable
					onClick={handleCtaClick}
					testId="rovo-non-mau-chat-cta-link"
					xcss={cx(styles.ctaLink)}
				>
					{formatMessage(messages.ctaLinkText)}
				</Pressable>
			</Stack>
		</Box>
	);
};
